<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";

  .no-data {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    flex-grow: 1;
  }

  .campaign-name {
    display: inline-block;
    @include mixins.body2();

    max-width: utils.in_rem(150px);
    &:not(.showTimes) {
      max-width: utils.in_rem(220px);
    }

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .activity-text {
    &:not(.skeleton) {
      @include mixins.body2();
      color: colors.get(black_90);
    }
  }
  .datetime-text {
    &:not(.skeleton) {
      @include mixins.body2();
      color: colors.get(black_90);
    }
  }
</style>

<script>
  import ActivityIcon from "@sourcewhale/shared/svelte-design-system/components/atoms/ActivityIcon.svelte";
  import Avatar from "@sourcewhale/shared/svelte-design-system/components/atoms/Avatar.svelte";
  import Link from "@sourcewhale/shared/svelte-design-system/components/atoms/Link.svelte";
  import Tooltip from "@sourcewhale/shared/svelte-design-system/components/atoms/Tooltip.svelte";
  import Table from "@sourcewhale/shared/svelte-design-system/components/molecules/Table/Table.svelte";
  import TBODY from "@sourcewhale/shared/svelte-design-system/components/molecules/Table/TBODY.svelte";
  import TD from "@sourcewhale/shared/svelte-design-system/components/molecules/Table/TD.svelte";
  import THEAD from "@sourcewhale/shared/svelte-design-system/components/molecules/Table/THEAD.svelte";
  import TR from "@sourcewhale/shared/svelte-design-system/components/molecules/Table/TR.svelte";
  import { activityFeedStore } from "../stores";
  import { formatActivityDate, formatActivityTime } from "../utils";
  import ActivityContent from "./ActivityContent.svelte";
  import NoActivityData from "./NoActivityData.svelte";

  export let data = [];
  export let selectedView = "pastActivity";
  // selectedView is used so we know what empty message to display, and to know
  // whether to show timestamps or not (scheduled activity should not show times)

  const { meta } = activityFeedStore;
  $: skeleton = $meta.fetching || !$meta.firstFetchDone;
  $: rowsData = skeleton
    ? Array.from({ length: 5 }).map(() => ({
        activityCode: "email",
        name: "Loading Activity...",
        user: { picture: "", name: "?" },
        timestamp: 1,
        campaignName: "Loading...",
      }))
    : data;
  $: noData = !rowsData.length;
  $: showTimes = selectedView === "pastActivity";
</script>

<!-- Place scroll bar in table so that header remains fixed -->
<div style={noData ? "" : "overflow: auto;"}>
  <Table>
    <THEAD>
      <TR>
        <TD>Activity</TD>
        <TD>User</TD>
        <TD>Campaign</TD>
        {#if showTimes}
          <TD>Date</TD>
          <TD>Time</TD>
        {/if}
      </TR>
    </THEAD>
    <TBODY>
      {#each rowsData as rowData}
        {@const { user = {} } = rowData}
        <TR>
          <TD>
            {#if rowData.activityCode || rowData.noteType}
              <ActivityIcon
                size="small"
                kind={rowData.activityCode || rowData.noteType}
                {skeleton}
              />
            {/if}
            {#if rowData.name}
              {@const reason = rowData.notInterestedReason?.title}
              {@const reasonParens = reason ? ` (${reason})` : ""}
              <span class="activity-text" class:skeleton>
                {rowData.name}{reasonParens}
              </span>
            {/if}
          </TD>
          <TD>
            {#if user.picture || user.name}
              {@const names = user.name?.split(" ") || []}
              {@const firstName = names[0] || ""}
              {@const lastName = names.at(-1) || ""}
              {#if skeleton}
                <Avatar
                  size="small"
                  src={user.picture}
                  {skeleton}
                  {firstName}
                  {lastName}
                />
              {:else}
                <Tooltip
                  placement="top"
                  let:onmouseenter
                  let:onmouseleave
                  let:onblur
                  let:onfocus
                >
                  <span slot="tooltip">{user.name || ""}</span>
                  <div
                    {onmouseenter}
                    {onmouseleave}
                    {onfocus}
                    {onblur}
                    role="button"
                    tabindex="-1"
                  >
                    <Avatar
                      size="small"
                      src={user.picture}
                      {skeleton}
                      {firstName}
                      {lastName}
                    />
                  </div>
                </Tooltip>
              {/if}
            {/if}
          </TD>
          <TD>
            {#if rowData.campaignName}
              {#if skeleton}
                <span class:skeleton>Loading...</span>
              {:else if rowData.campaignId && rowData.campaignName !== "Private Campaign"}
                <Link
                  href={`/campaign?campaignId=${rowData.campaignId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onclick={(e) => e.stopPropagation()}
                  style="display: flex;"
                >
                  <span class="campaign-name" class:showTimes
                    >{rowData.campaignName}</span
                  >
                </Link>
              {:else}
                <span class="campaign-name" class:showTimes
                  >{rowData.campaignName}</span
                >
              {/if}
            {/if}
          </TD>
          {#if showTimes}
            <TD>
              {#if rowData.timestamp}
                <span class="datetime-text" class:skeleton
                  >{formatActivityDate(rowData.timestamp)}</span
                >
              {/if}
            </TD>
            <TD>
              {#if rowData.timestamp}
                <span class="datetime-text" class:skeleton
                  >{formatActivityTime(rowData.timestamp)}</span
                >
              {/if}
            </TD>
          {/if}
        </TR>
        {#if rowData.content || rowData.subject || rowData.to || rowData.title}
          <TR isChild={true}>
            <TD colspan={showTimes ? 5 : 3}>
              <ActivityContent activity={rowData} />
            </TD>
          </TR>
        {/if}
      {/each}
    </TBODY>
  </Table>
</div>
<!-- Append at the end here so it is vertically centred in the modal -->
{#if noData}
  <div class="no-data">
    <NoActivityData {selectedView} />
  </div>
{/if}
